<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Servicios</h1>
      <v-btn rounded color="primary" depressed @click="openCreateForActiveTab">
        <v-icon left>mdi-plus</v-icon>
        {{ createButtonLabel }}
      </v-btn>
    </v-row>

    <v-tabs v-model="activeTab">
      <v-tab>Servicios</v-tab>
      <v-tab>Tipos de Servicio</v-tab>
      <v-tab>Tipos de Adjunto</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-card class="pa-4 my-4" outlined>
          <v-row class="ma-0" align="center">
            <v-col cols="12" md="5">
              <v-text-field
                v-model="searchText"
                prepend-inner-icon="mdi-magnify"
                placeholder="Buscar por título"
                outlined
                dense
                hide-details
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="6" md="2">
              <v-select
                v-model="typeFilter"
                :items="typeFilterOptions"
                item-text="text"
                item-value="value"
                label="Tipo de servicio"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="6" md="2">
              <v-select
                v-model="activeFilter"
                :items="activeOptions"
                item-text="text"
                item-value="value"
                label="Activo"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="6" md="3">
              <v-select
                v-model="languageCountFilter"
                :items="languageCountFilterOptions"
                item-text="text"
                item-value="value"
                label="Cantidad de idiomas"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
          </v-row>
        </v-card>

        <v-data-table
          :headers="headers"
          :items="filteredServices"
          :loading="loadingData"
          sort-by="title"
          class="elevation-1"
        >
          <template v-slot:item.ref="{ item }">
            <span class="text-no-wrap">{{ item.ref || '—' }}</span>
          </template>
          <template v-slot:item.iconId="{ item }">
            <div class="d-flex justify-center py-1">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-img
                    v-if="item.iconId"
                    v-bind="attrs"
                    v-on="on"
                    :src="iconAssetPath(item.iconId)"
                    max-height="24"
                    max-width="24"
                    contain
                  ></v-img>
                  <span v-else v-bind="attrs" v-on="on" class="grey--text">—</span>
                </template>
                <span>{{ item.iconId || 'Sin ícono' }}</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.serviceType="{ item }">
            <span>{{ item.serviceType ? item.serviceType.name : '—' }}</span>
          </template>
          <template v-slot:item.active="{ item }">
            <v-icon color="green" v-if="item.active">mdi-check-circle</v-icon>
            <v-icon color="red" v-else>mdi-close-circle</v-icon>
          </template>
          <template v-slot:item.languages="{ item }">
            <div class="d-flex flex-wrap py-1">
              <template v-if="languageCount(item)">
                <v-chip
                  v-for="lang in visibleLanguages(item)"
                  :key="lang"
                  x-small
                  class="mr-1 mb-1"
                >
                  {{ lang.toUpperCase() }}
                </v-chip>
                <v-chip
                  v-if="extraLanguagesCount(item) > 0"
                  x-small
                  class="mr-1 mb-1"
                  color="grey lighten-2"
                >
                  +{{ extraLanguagesCount(item) }}
                </v-chip>
              </template>
              <span v-else class="text-caption grey--text">Sin traducciones</span>
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-2"
                  fab
                  dark
                  depressed
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  color="orange"
                  @click="openDialog(item)"
                >
                  <v-icon small dark>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Editar</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-2"
                  fab
                  dark
                  depressed
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  @click="openDefaultsDialog(item)"
                >
                  <v-icon small dark>mdi-earth</v-icon>
                </v-btn>
              </template>
              <span>Configurar defaults</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-2"
                  fab
                  dark
                  depressed
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  color="teal"
                  @click="openTranslationsDialog(item)"
                >
                  <v-icon small dark>mdi-translate</v-icon>
                </v-btn>
              </template>
              <span>Traducciones</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-tab-item>

      <v-tab-item>
        <service-types-tab ref="typesTab" @changed="getServices"></service-types-tab>
      </v-tab-item>

      <v-tab-item>
        <attachment-types-tab ref="attachmentTypesTab"></attachment-types-tab>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog
      v-model="showDialog"
      min-width="600"
      max-width="900"
      min-height="600"
      max-height="900"
      @keydown.esc="closeDialog"
    >
      <template>
        <service-maintainer
          :service="selectedService"
          @update-list="onServiceSaved"
          @close-dialog="closeDialog"
        ></service-maintainer>
      </template>
    </v-dialog>

    <v-dialog v-model="showDefaultsDialog" max-width="900" @keydown.esc="closeDefaultsDialog">
      <service-defaults-dialog
        v-if="showDefaultsDialog"
        :service-id="selectedDefaultsService ? selectedDefaultsService.id : null"
        :service-name="selectedDefaultsService ? selectedDefaultsService.title : ''"
        @close-dialog="closeDefaultsDialog"
      ></service-defaults-dialog>
    </v-dialog>

    <v-dialog v-model="showTranslationsDialog" max-width="900" @keydown.esc="closeTranslationsDialog">
      <service-translations-dialog
        v-if="showTranslationsDialog"
        :service-id="selectedTranslationsService ? selectedTranslationsService.id : null"
        :service-name="selectedTranslationsService ? selectedTranslationsService.title : ''"
        @close-dialog="closeTranslationsDialog"
      ></service-translations-dialog>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ServiceMaintainer from '../components/ServiceMaintainer.vue';
import ServiceTypesTab from '../components/ServiceTypesTab.vue';
import AttachmentTypesTab from '../components/AttachmentTypesTab.vue';
import ServiceDefaultsDialog from '../components/ServiceDefaultsDialog.vue';
import ServiceTranslationsDialog from '../components/ServiceTranslationsDialog.vue';
import ServicesService from '../services/services.service';
import { utilsMixin } from '../mixins/utilsMixin';

export default {
  name: 'Servicios',
  components: {
    ServiceMaintainer,
    ServiceTypesTab,
    AttachmentTypesTab,
    ServiceDefaultsDialog,
    ServiceTranslationsDialog
  },
  mixins: [utilsMixin],
  data() {
    return {
      activeTab: 0,
      loadingData: false,
      showDialog: false,
      showDefaultsDialog: false,
      showTranslationsDialog: false,
      selectedService: null,
      selectedDefaultsService: null,
      selectedTranslationsService: null,
      services: [],
      searchText: '',
      typeFilter: null,
      activeFilter: null,
      languageCountFilter: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'red',
      activeOptions: [
        { text: 'Todos', value: null },
        { text: 'Activos', value: true },
        { text: 'Inactivos', value: false }
      ],
      maxVisibleLanguageChips: 5,
      headers: [
        { text: 'Ref', value: 'ref', sortable: true, width: '100px' },
        { text: 'Ícono', value: 'iconId', sortable: false, align: 'center', width: '80px' },
        { text: 'Título', value: 'title', sortable: true },
        { text: 'Tipo de servicio', value: 'serviceType', sortable: false },
        { text: 'Orden', value: 'order', sortable: false },
        { text: 'Activo', value: 'active', sortable: false, align: 'center' },
        { text: 'Idiomas', value: 'languages', sortable: false, width: '220px' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'end' }
      ]
    };
  },
  computed: {
    createButtonLabel() {
      if (this.activeTab === 1) return 'Tipo de servicio';
      if (this.activeTab === 2) return 'Tipo de adjunto';
      return 'Nuevo servicio';
    },
    typeFilterOptions() {
      const map = new Map();
      this.services.forEach(service => {
        if (service.serviceType && service.serviceType.id) {
          map.set(service.serviceType.id, service.serviceType.name);
        }
      });
      const options = Array.from(map, ([value, text]) => ({ value, text }));
      options.sort((a, b) => a.text.localeCompare(b.text));
      return [{ text: 'Todos', value: null }, ...options];
    },
    // Filter operates on COUNT of translated languages, not on which specific
    // language — options are built from the counts actually present in the
    // data (e.g. some services have 0, some 1, some 2...).
    languageCountFilterOptions() {
      const counts = new Set(this.services.map(service => this.languageCount(service)));
      const options = Array.from(counts)
        .sort((a, b) => a - b)
        .map(count => ({
          value: count,
          text: count === 0 ? 'Sin traducciones' : `${count} idioma${count === 1 ? '' : 's'}`
        }));
      return [{ text: 'Todos', value: null }, ...options];
    },
    filteredServices() {
      const search = (this.searchText || '').trim().toLowerCase();
      return this.services.filter(service => {
        const matchesSearch = !search || (service.title || '').toLowerCase().includes(search);
        const matchesType =
          this.typeFilter == null || (service.serviceType && service.serviceType.id === this.typeFilter);
        const matchesActive = this.activeFilter == null || service.active === this.activeFilter;
        const matchesLanguageCount =
          this.languageCountFilter == null || this.languageCount(service) === this.languageCountFilter;
        return matchesSearch && matchesType && matchesActive && matchesLanguageCount;
      });
    }
  },
  created() {
    this.getServices();
  },
  methods: {
    // Íconos servidos como estáticos en public/icons/services/<iconId>.svg
    // (mismo helper que ServiceMaintainer.vue).
    iconAssetPath(iconId) {
      return `${process.env.BASE_URL}icons/services/${iconId}.svg`;
    },
    languageCount(service) {
      return (service.translatedLanguages || []).length;
    },
    visibleLanguages(service) {
      return (service.translatedLanguages || []).slice(0, this.maxVisibleLanguageChips);
    },
    extraLanguagesCount(service) {
      return Math.max(this.languageCount(service) - this.maxVisibleLanguageChips, 0);
    },
    async getServices() {
      this.loadingData = true;
      try {
        const res = await ServicesService.getAllServices();
        this.services = res.data;
      } catch (e) {
        this.showErrorSnackbar('Error al cargar los servicios');
      }
      this.loadingData = false;
    },
    openCreateForActiveTab() {
      if (this.activeTab === 0) {
        this.openDialog(null);
      } else if (this.activeTab === 1 && this.$refs.typesTab) {
        this.$refs.typesTab.openMaintainerDialog(null);
      } else if (this.activeTab === 2 && this.$refs.attachmentTypesTab) {
        this.$refs.attachmentTypesTab.openMaintainerDialog(null);
      }
    },
    openDialog(service) {
      this.selectedService = service || null;
      this.showDialog = true;
    },
    closeDialog() {
      this.showDialog = false;
      this.selectedService = null;
    },
    onServiceSaved() {
      this.showSuccessSnackbar('Servicio guardado correctamente');
      this.getServices();
    },
    openDefaultsDialog(service) {
      this.selectedDefaultsService = service;
      this.showDefaultsDialog = true;
    },
    closeDefaultsDialog() {
      this.showDefaultsDialog = false;
      this.selectedDefaultsService = null;
    },
    openTranslationsDialog(service) {
      this.selectedTranslationsService = service;
      this.showTranslationsDialog = true;
    },
    closeTranslationsDialog() {
      this.showTranslationsDialog = false;
      this.selectedTranslationsService = null;
    }
  }
};
</script>
